import { Typography, styled } from "@mui/material";
import { Header } from "Components/Header/header";
import { useLayoutContext } from "Providers/LayoutProvider";
import { motion } from "motion/react";
import { Box } from "@mui/system";
import { useProjectTypeTransition } from "features/project";
import { CenterPage } from "Components/CenterPage/CenterPage";
import { memo, useState } from "react";
import SideMenu from "features/leftMenu/components/SideMenu";
import { useReports } from "Hooks/useReports";
import { useIsMobile } from "Hooks/useIsMobile";
import TopBar from "features/leftMenu/components/TopBar";
import { TOP_BAR_HEIGHT } from "features/leftMenu/constants";
import SiteinfoEditor from "features/ai/components/SiteinfoEditor";
import { matchPath, useLocation } from "react-router-dom";
import ProjectDashboard from "features/project/components/ProjectDashboard";

const DashboardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "fullHeight",
})<{ fullHeight?: boolean }>(({ theme, fullHeight }) => ({
  margin: theme.spacing(0),
  display: "flex",
  height: fullHeight ? "100vh" : `calc(100vh - ${TOP_BAR_HEIGHT}px)`,
  [theme.breakpoints.down("md")]: {
    display: "flex",
    overflow: "auto",
  },
  background: theme.palette.primary.main,
  overflow: "hidden",
}));

const MainContent = styled(Box)(({ theme }) => ({
  minWidth: 0,
  flex: 1,
  [theme.breakpoints.down("md")]: {
    display: "block",
  },
  position: "relative",
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  overflowX: "hidden",
  backgroundColor: theme.palette.background.default,
}));

const SideBar = styled(Box)(({ theme }) => ({
  maxWidth: 356,
  background: theme.palette.background.default,
  height: `calc(100vh - ${TOP_BAR_HEIGHT}px)`,
  boxShadow: `inset 2px 0 0 #d8e0f8, inset 6px 0 0 #9ea4c6`,
}));

const ContentArea = styled("div", {
  shouldForwardProp: (prop) => prop !== "noPadding",
})<{ noPadding?: boolean }>(({ theme, noPadding }) => ({
  padding: noPadding ? 0 : theme.spacing(0, 4),
  flex: 1,
}));
const DashboardLayout = ({
  children,
  hideNav,
  hideBreadcrumbs,
  hideUser,
}: {
  children: React.ReactNode;
  hideNav?: boolean;
  hideBreadcrumbs?: boolean;
  hideUser?: boolean;
}) => {
  const { contentRight } = useLayoutContext();
  const projectTypeTransitioning = useProjectTypeTransition();
  const { reports, fetched: reportsFetched } = useReports();
  const [isMouseOver, setIsMouseOver] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  const isGetStarted = Boolean(
    matchPath(location.pathname, {
      path: "/clients/:clientId/projects/:projectId/get-started",
      exact: true,
      strict: false,
    }),
  );

  const variants = {
    open: {
      opacity: 1,
      width: contentRight,
      height: "auto",
      transition: {
        opacity: {
          duration: 0.8,
        },
        width: {
          duration: 0.2,
        },
        height: {
          duration: 0,
        },
      },
    },
    closed: {
      opacity: 0,
      width: 0,
      overflow: "hidden",
      height: 0,
      transition: {
        opacity: {
          duration: 0.1,
        },
        width: {
          duration: 0.3,
        },
        height: {
          duration: 0,
          delay: 0.3,
        },
      },
    },
  };

  if (isMobile && !isGetStarted) {
    return <ProjectDashboard />;
  }

  if (projectTypeTransitioning) {
    return (
      <CenterPage calcHeight>
        <Typography variant="h1" mb={1}>
          Project Type Updated!
        </Typography>
        <Typography>
          The UI is now adapting to reflect your new selection. Regenerating
          data, please wait a moment.
        </Typography>
      </CenterPage>
    );
  }

  return (
    <motion.div
      key="dash"
      initial="closed"
      animate={contentRight > 0 ? "open" : "closed"}
    >
      <TopBar noLinks={hideNav} />

      <DashboardContainer
        fullHeight={isGetStarted}
        p={hideNav ? 1 : 0}
        pl={hideNav ? 1 : 0}
      >
        <Box
          display="flex"
          onMouseEnter={() => setIsMouseOver(true)}
          onMouseLeave={() => setIsMouseOver(false)}
        >
          <SideMenu
            reports={reports}
            reportsFetched={reportsFetched}
            mouseOver={isMouseOver}
          />
        </Box>

        <MainContent>
          {!isGetStarted && <Header hideBreadcrumbs={hideBreadcrumbs} />}
          <ContentArea noPadding={isGetStarted}>{children}</ContentArea>
        </MainContent>
        <SideBar id="rightSideBar">
          <motion.div key="right-side" variants={variants}></motion.div>
        </SideBar>
      </DashboardContainer>

      <SiteinfoEditor />
    </motion.div>
  );
};

export default memo(DashboardLayout);
